import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  JustifyContent,
  Text,
  TextAlign,
  Grid,
  Col,
  Space,
  IconVariant,
  Icon,
  FontSize,
  RadiusScale,
} from '@gousto-internal/citrus-react'

import { Cta, useGetCtaProps } from 'routes/Home/Cta'
import {
  comparisonTableTexts,
  BLOCK_WIDTH_LIST,
  recipeBrandsData,
} from 'routes/Home/PriceComparisonTable/constants'

import { ImageContainer } from '../Containers.styled'
import { DividerWithText } from '../DividerWithText/DividerWithText'
import { TableItemPicture } from '../TableItemPicture/TableItemPicture'

// swap this out when Citrus Pill is ready
const Pill = ({ text }: { text: string }) => (
  <Box
    display={Display.Flex}
    alignItems={AlignItems.Center}
    bg={Color.Success_50}
    paddingH={2}
    paddingV={1}
    width="max-content"
    borderRadius={8 as RadiusScale}
  >
    <Icon name="success" size={3} variant={IconVariant.Confirmation} />
    <Space size={0.5} direction="horizontal" />
    <Text color={Color.Success_900} size={0 as FontSize} fontWeight={FontWeight.Bold}>
      {text}
    </Text>
  </Box>
)

export const ComparisonTable = () => {
  const getTextColor = (brandName: string) =>
    brandName === 'Gousto' ? Color.Success_800 : Color.Error_900

  const { ctaUri, ctaText } = useGetCtaProps({ withDiscount: true })

  return (
    <Box
      maxWidth={BLOCK_WIDTH_LIST}
      paddingV={[0, 0, 5, 5]}
      width="100%"
      data-testid="ComparisonTableContainer"
    >
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
      >
        <Grid width="100%" alignItems={AlignItems.Center} data-testid="ComparisonTableLogos">
          {recipeBrandsData.map(({ brandName, nextGenImage, fallbackImage }) => (
            <Col
              size={4}
              key={`${brandName}-logo`}
              display={Display.Flex}
              justifyContent={JustifyContent.Center}
              paddingBottom={2}
            >
              <ImageContainer display={Display.Flex} maxWidth="96px" maxHeight="52px">
                <TableItemPicture
                  nextGenImage={nextGenImage}
                  fallbackImage={fallbackImage}
                  altText={`${brandName}-logo`}
                />
              </ImageContainer>
            </Col>
          ))}
        </Grid>
        <DividerWithText text="Average box price (before discount)" />
        <Space size={2} />
        <Grid width="100%" data-testid="ComparisonTablePrices">
          {recipeBrandsData.map(({ brandName, price }) => (
            <Col
              size={4}
              key={`${brandName}-price`}
              display={Display.Flex}
              justifyContent={JustifyContent.Center}
            >
              <Box
                display={Display.Flex}
                flexDirection={FlexDirection.Column}
                alignItems={AlignItems.Center}
              >
                <Text size={5} fontWeight={FontWeight.Bold} color={getTextColor(brandName)}>
                  £{price}
                </Text>
                <Text size={2} color={getTextColor(brandName)}>
                  Per box
                </Text>
                <Space size={1} />
                {brandName === 'Gousto' && <Pill text="Best value" />}
                <Space size={3} />
              </Box>
            </Col>
          ))}
        </Grid>
        <DividerWithText text="Choose from" />
        <Space size={2} />
        <Grid width="100%" data-testid="ComparisonTableRecipes">
          {recipeBrandsData.map(({ brandName, noOfRecipes }) => (
            <Col
              size={4}
              key={`${brandName}-noOfRecipes`}
              display={Display.Flex}
              justifyContent={JustifyContent.Center}
            >
              <Box
                display={Display.Flex}
                flexDirection={FlexDirection.Column}
                alignItems={AlignItems.Center}
              >
                <Text size={5} fontWeight={FontWeight.Bold} color={getTextColor(brandName)}>
                  {noOfRecipes}+
                </Text>
                <Text size={2} color={getTextColor(brandName)}>
                  Recipes
                </Text>
                <Space size={1} />
                {brandName === 'Gousto' && <Pill text="Most choice" />}
              </Box>
            </Col>
          ))}
        </Grid>
        <Space size={5} />
        {/* between 639px and 959px we show CTA with the table  */}
        <Box display={[Display.None, Display.Block, Display.None]}>
          <Cta ctaUri={ctaUri} trackingSectionName="priceComparison">
            {ctaText}
          </Cta>
        </Box>
        <Text size={0 as FontSize} textAlign={TextAlign.Center}>
          {comparisonTableTexts.tableFooter}
          <a href={comparisonTableTexts.tableFooterLink.href}>
            &nbsp;{comparisonTableTexts.tableFooterLink.text}
          </a>
        </Text>
      </Box>
    </Box>
  )
}
